import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, Check } from "lucide-react";
import { joinWaitlist } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { first_name: "", email: "", zip_code: "", ev_owner: "" };

const EV_OPTIONS = [
  { value: "yes", label: "Yes, I own an EV" },
  { value: "no", label: "No" },
  { value: "planning", label: "Planning to buy" },
];

export default function WaitlistForm({ compact = false }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await joinWaitlist(form);
      setDone(true);
      setForm(empty);
      toast.success(res.message || "You're on the list. Welcome to RestPower.");
      if (window.posthog) {
        try { window.posthog.capture("waitlist_signup", { source: "better-stop" }); } catch (_) {}
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-xl border border-white/12 bg-rp-card px-4 py-3.5 text-sm text-white placeholder:text-white/35 outline-none transition-colors duration-200 focus:border-rp-accent focus:ring-1 focus:ring-rp-accent";
  const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.14em] text-white/50";

  if (done) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-rp-accent/30 bg-rp-card p-10 text-center shadow-glow-sm" data-testid="waitlist-success">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-rp-accent text-black">
          <Check className="h-7 w-7" />
        </span>
        <h3 className="font-display text-2xl font-medium tracking-tight text-white">You're on the list. Welcome to RestPower.</h3>
        <p className="max-w-sm text-sm text-white/55">
          We'll be among the first to let you know when RestPower is coming to your area.
        </p>
        <button
          onClick={() => setDone(false)}
          className="mt-2 font-mono text-xs uppercase tracking-[0.14em] text-rp-accent hover:text-white"
          data-testid="waitlist-add-another"
        >
          Add another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="waitlist-form">
      <div className={`grid gap-5 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
        <div>
          <label className={labelCls}>First Name *</label>
          <input required value={form.first_name} onChange={set("first_name")} placeholder="Your first name" className={field} data-testid="waitlist-firstname" />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={field} data-testid="waitlist-email" />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>ZIP Code</label>
          <input value={form.zip_code} onChange={set("zip_code")} placeholder="e.g. 30301" className={field} data-testid="waitlist-zip" inputMode="numeric" />
        </div>
        <div>
          <label className={labelCls}>EV Owner?</label>
          <Select value={form.ev_owner} onValueChange={(v) => setForm((f) => ({ ...f, ev_owner: v }))}>
            <SelectTrigger
              data-testid="waitlist-evowner"
              className="h-[50px] rounded-xl border-white/12 bg-rp-card px-4 text-sm text-white focus:border-rp-accent focus:ring-1 focus:ring-rp-accent data-[placeholder]:text-white/35"
            >
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-rp-surface text-white">
              {EV_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-white focus:bg-rp-accent focus:text-black" data-testid={`waitlist-ev-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="waitlist-submit"
        className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-rp-accent px-8 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-black transition-colors duration-200 hover:bg-white disabled:opacity-70"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {loading ? "Joining" : "Join the Waitlist"}
        {!loading && <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />}
      </button>
      <p className="text-center font-mono text-[11px] tracking-wide text-white/35">
        No spam. Just updates on our journey and future locations.
      </p>
    </form>
  );
}
